import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { getViz, type VizPayload } from "@/services/workbench_api";

const POLL_INTERVAL_MS = 500;
const POLL_MAX_ATTEMPTS = 20; // 20 × 500 ms = 10 s ceiling

type Params = {
  flavorId: string | null;    // UUID
  formulaId: number | null;
  versionId: number | null;
};

export function useWorkbenchViz({ flavorId, formulaId, versionId }: Params) {
  return useQuery<VizPayload>({
    queryKey: ["viz", versionId],
    queryFn: () => getViz(flavorId!, formulaId!, versionId!),
    enabled: flavorId != null && formulaId != null && versionId != null,
    // Infinity, not 0 — the save endpoint computes viz inline and
    // pre-populates this cache via setQueryData(['viz', N], payload) in
    // WorkbenchPage's onVersionSaved. With staleTime: 0 React Query
    // considered the freshly-set data stale and scheduled a background
    // /viz/N GET that returned byte-identical data. The polling for the
    // rare server-side stale-pin recompute case is driven separately by
    // refetchInterval below and is unaffected by staleTime.
    staleTime: Infinity,
    // React Query v5: refetchInterval receives the Query object directly.
    // We stop polling once the job is no longer in-flight, or after the
    // bounded attempt ceiling is reached.
    refetchInterval: (q) => {
      const data = q.state.data;
      if (!data) return false;
      if (data.status !== "computing" && data.status !== "pending") return false;
      if (q.state.dataUpdateCount >= POLL_MAX_ATTEMPTS) return false;
      return POLL_INTERVAL_MS;
    },
    // While the autosave swaps `versionId`, the queryKey changes and React
    // Query would normally drop `data` to undefined for the new key slot.
    // SensoryAnalysisRail falls into a "Loading…" overlay then and the
    // entire scatter plot remounts. keepPreviousData keeps the prior
    // version's payload visible so the static parts (target, neighbors,
    // axes) don't flash; only the dynamic parts (current coord, loss,
    // tasteRadar) diff in once the new payload lands.
    placeholderData: keepPreviousData,
  });
}
